import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import GooglePlacesAutocomplete, {
  geocodeByPlaceId,
} from "react-google-places-autocomplete";
import { useTranslation } from "react-i18next";
import DragDropImagePicker from "@/shared/dragDropImage";
import { FileTypes } from "@/shared/utils/enum";
import LocationPickerPopup, {
  LocationData,
} from "@/components/shared/location/LocationPickerPopup";
import { LocationPinIcon } from "@/components/assets/icons/svgs";
import { api } from "@/utils/api";
import type { RequestQuoteDraftForm } from "@/utils/requestQuoteDraft";
import {
  getRequestQuoteImageFiles,
  setRequestQuoteImageFiles,
} from "@/utils/requestQuoteDraft";

const googlePlacesApiKey = import.meta.env.VITE_GOOGLE_PLACES_API_KEY;

export const REQUEST_QUOTE_FORM_ID = "request-quote-compose-form";

type Props = {
  initialForm: RequestQuoteDraftForm;
  preferredServiceId?: string;
  onSave: (form: RequestQuoteDraftForm, files: File[]) => void;
  submitLabel: string;
  /** desktop = light sidebar form; mobile = blue full-bleed form */
  variant?: "desktop" | "mobile";
  /** Blue brand panel (desktop sidebar on /request-quote) */
  brandSurface?: boolean;
  /** When true, hide the inline submit (use sticky bar with form= attribute) */
  hideSubmit?: boolean;
  /** Called when submit is blocked by validation (e.g. clear pending continue) */
  onSaveAborted?: () => void;
};

const getMinTime = (date: Date | null) => {
  if (!date) return undefined;
  const now = new Date();
  const selected = new Date(date);
  if (
    selected.getDate() === now.getDate() &&
    selected.getMonth() === now.getMonth() &&
    selected.getFullYear() === now.getFullYear()
  ) {
    return new Date(
      selected.getFullYear(),
      selected.getMonth(),
      selected.getDate(),
      now.getHours(),
      now.getMinutes()
    );
  }
  return new Date(
    selected.getFullYear(),
    selected.getMonth(),
    selected.getDate(),
    0,
    0
  );
};

const getMaxTime = (date: Date | null) => {
  if (!date) return undefined;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59);
};

type FormFieldErrors = {
  selectedCategoryId?: string;
  selectedService?: string;
  description?: string;
  location?: string;
  startDate?: string;
};

function FieldError({
  message,
  onBrandSurface,
}: {
  message?: string;
  onBrandSurface?: boolean;
}) {
  if (!message) return null;
  return (
    <p
      className={`mt-1 pl-2 text-xs ${
        onBrandSurface ? "text-red-200" : "text-red-500"
      }`}
      role="alert"
    >
      {message}
    </p>
  );
}

export default function RequestQuoteFormPanel({
  initialForm,
  preferredServiceId,
  onSave,
  submitLabel,
  variant = "desktop",
  brandSurface = false,
  hideSubmit = false,
  onSaveAborted,
}: Props) {
  const { t, i18n } = useTranslation();
  const isMobile = variant === "mobile";
  const onBrandSurface = isMobile || brandSurface;

  const [serviceCategories, setServiceCategories] = useState<any[]>([]);
  const [categoryServices, setCategoryServices] = useState<any[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState(
    initialForm.selectedCategoryId || ""
  );
  const [selectedService, setSelectedService] = useState(
    initialForm.selectedService || ""
  );
  const [description, setDescription] = useState(initialForm.description || "");
  const [budgetDetails, setBudgetDetails] = useState(
    initialForm.budgetDetails || ""
  );
  const [urgency, setUrgency] = useState<"low" | "medium" | "high">(
    initialForm.urgency || "low"
  );
  const [startDate, setStartDate] = useState<Date | null>(
    initialForm.startDateISO ? new Date(initialForm.startDateISO) : null
  );
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(
    initialForm.location
      ? {
          address: initialForm.location.address,
          lat: initialForm.location.lat,
          lng: initialForm.location.lng,
        }
      : null
  );
  const [googlePlace, setGooglePlace] = useState<any>(
    initialForm.location
      ? { label: initialForm.location.address, value: initialForm.location.address }
      : null
  );
  const [showLocationPopup, setShowLocationPopup] = useState(false);
  const [selectedImages, setSelectedImages] = useState<File[]>(() =>
    getRequestQuoteImageFiles()
  );
  const [imagePreviews, setImagePreviews] = useState<string[]>(
    initialForm.imagePreviewUrls || []
  );
  const [fieldErrors, setFieldErrors] = useState<FormFieldErrors>({});

  const clearFieldError = (field: keyof FormFieldErrors) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const invalidControlClass = (field: keyof FormFieldErrors) =>
    fieldErrors[field]
      ? onBrandSurface
        ? "ring-2 ring-red-300"
        : "border-red-400 focus:border-red-400"
      : "";

  useEffect(() => {
    (async () => {
      try {
        const cats = await api.getServiceCategories();
        setServiceCategories(cats || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, [i18n.language]);

  useEffect(() => {
    const selectedCategory = serviceCategories.find(
      (cat) => cat._id === selectedCategoryId
    );
    const services = selectedCategory?.services || [];
    setCategoryServices(services);

    if (
      preferredServiceId &&
      !selectedService &&
      services.some((s: any) => s._id === preferredServiceId)
    ) {
      setSelectedService(preferredServiceId);
    }
  }, [selectedCategoryId, serviceCategories, preferredServiceId, selectedService]);

  useEffect(() => {
    if (!preferredServiceId || selectedCategoryId || !serviceCategories.length)
      return;
    for (const cat of serviceCategories) {
      if (cat.services?.some((s: any) => s._id === preferredServiceId)) {
        setSelectedCategoryId(cat._id);
        setSelectedService(preferredServiceId);
        break;
      }
    }
  }, [preferredServiceId, serviceCategories, selectedCategoryId]);

  const handleGooglePlaceSelect = async (place: any) => {
    setGooglePlace(place);
    if (!place?.value?.place_id) {
      setSelectedLocation(null);
      return;
    }
    try {
      const results = await geocodeByPlaceId(place.value.place_id);
      const loc = results[0]?.geometry?.location;
      if (!loc) return;
      setSelectedLocation({
        address: place.label || results[0].formatted_address,
        lat: loc.lat(),
        lng: loc.lng(),
      });
      clearFieldError("location");
    } catch (e) {
      console.error(e);
      setFieldErrors((prev) => ({
        ...prev,
        location: t("something_went_wrong") || "Could not resolve location",
      }));
    }
  };

  const handleImageChange = (files: File[]) => {
    const next = [...selectedImages, ...files].slice(0, 5);
    setSelectedImages(next);
    setRequestQuoteImageFiles(next);
    const previews = next.map((f) => URL.createObjectURL(f));
    setImagePreviews(previews);
  };

  const handleRemoveImage = (idx: number) => {
    const next = selectedImages.filter((_, i) => i !== idx);
    setSelectedImages(next);
    setRequestQuoteImageFiles(next);
    setImagePreviews(next.map((f) => URL.createObjectURL(f)));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: FormFieldErrors = {};

    if (!selectedCategoryId) {
      nextErrors.selectedCategoryId =
        t("selectCategory") || "Please select a category.";
    }
    if (!selectedService) {
      nextErrors.selectedService =
        t("selectServiceOffered") || "Please select a service.";
    }
    if (!description.trim()) {
      nextErrors.description =
        t("description_required") || "Please add a description.";
    }
    if (!selectedLocation) {
      nextErrors.location =
        t("serviceLocation") || "Please select a location.";
    }
    if (!startDate) {
      nextErrors.startDate =
        t("select_start_date_time") || "Please select a date.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      onSaveAborted?.();
      const firstInvalid = document.querySelector(
        "[data-request-quote-invalid='true']"
      );
      firstInvalid?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setFieldErrors({});

    const categoryName =
      serviceCategories.find((c) => c._id === selectedCategoryId)?.name || "";
    const serviceName =
      categoryServices.find((s) => s._id === selectedService)?.name || "";

    onSave(
      {
        description: description.trim(),
        budgetDetails,
        urgency,
        selectedCategoryId,
        selectedService,
        categoryName,
        serviceName,
        startDateISO: startDate.toISOString(),
        location: selectedLocation
          ? {
              address: selectedLocation.address,
              lat: selectedLocation.lat,
              lng: selectedLocation.lng,
            }
          : null,
        imagePreviewUrls: imagePreviews,
      },
      selectedImages
    );
  };

  const labelClass = onBrandSurface
    ? "block text-xs font-medium text-white mb-1 pl-2"
    : "block text-sm tracking-wider uppercase text-[#424654] mb-2";

  const controlClass = onBrandSurface
    ? "w-full h-12 rounded-lg bg-white px-4 text-base text-[#111c2d] shadow-sm border-0 outline-none"
    : "w-full rounded-lg border border-[#d8e3fb] bg-[#f9f9ff] px-[17px] py-[13px] text-sm text-[#111c2d] outline-none focus:border-[#4285F4]";

  const urgencyOptions: Array<"high" | "medium" | "low"> = [
    "high",
    "medium",
    "low",
  ];

  return (
    <>
      <LocationPickerPopup
        open={showLocationPopup}
        onClose={() => setShowLocationPopup(false)}
        onSelect={(loc) => {
          setSelectedLocation(loc);
          setGooglePlace({ label: loc.address, value: loc.address });
          setShowLocationPopup(false);
          clearFieldError("location");
        }}
      />
      <form
        id={REQUEST_QUOTE_FORM_ID}
        onSubmit={handleSubmit}
        className={`flex flex-col ${isMobile ? "gap-6" : "gap-4"}`}
      >
        <div
          data-request-quote-invalid={fieldErrors.selectedCategoryId ? "true" : undefined}
        >
          <label className={labelClass}>
            {t("serviceCategory")} <span className="text-red-500">*</span>
          </label>
          <select
            className={`${controlClass} ${invalidControlClass("selectedCategoryId")}`}
            required
            value={selectedCategoryId}
            onChange={(e) => {
              setSelectedCategoryId(e.target.value);
              setSelectedService("");
              clearFieldError("selectedCategoryId");
              clearFieldError("selectedService");
            }}
          >
            <option value="" disabled hidden>
              {t("selectCategory")}
            </option>
            {serviceCategories.map((category) => (
              <option key={category._id} value={category._id}>
                {category.name}
              </option>
            ))}
          </select>
          <FieldError
            message={fieldErrors.selectedCategoryId}
            onBrandSurface={onBrandSurface}
          />
        </div>

        <div
          data-request-quote-invalid={fieldErrors.selectedService ? "true" : undefined}
        >
          <label className={labelClass}>
            {t("service")} <span className="text-red-500">*</span>
          </label>
          <select
            className={`${controlClass} ${invalidControlClass("selectedService")}`}
            required
            value={selectedService}
            onChange={(e) => {
              setSelectedService(e.target.value);
              clearFieldError("selectedService");
            }}
            disabled={!selectedCategoryId}
          >
            <option value="" disabled hidden>
              {t("selectServiceOffered")}
            </option>
            {categoryServices.map((service) => (
              <option key={service._id} value={service._id}>
                {service.name}
              </option>
            ))}
          </select>
          <FieldError
            message={fieldErrors.selectedService}
            onBrandSurface={onBrandSurface}
          />
        </div>

        <div
          data-request-quote-invalid={fieldErrors.description ? "true" : undefined}
        >
          <label className={labelClass}>
            {t("descriptionofRequestedService")}{" "}
            <span className="text-red-500">*</span>
          </label>
          <textarea
            name="description"
            required
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              clearFieldError("description");
            }}
            placeholder={
              t("descriptionofRequestedService") ||
              "Briefly describe what you need help with..."
            }
            className={`${controlClass} ${invalidControlClass("description")} ${
              onBrandSurface ? "!h-auto min-h-[128px] py-4" : "min-h-[128px] resize-y"
            } placeholder:text-[#c3c6d6]`}
          />
          <FieldError
            message={fieldErrors.description}
            onBrandSurface={onBrandSurface}
          />
        </div>

        <div>
          <label className={labelClass}>
            {t("estimatedBudget")} ({t("optional")})
          </label>
          <select
            className={controlClass}
            value={budgetDetails}
            onChange={(e) => setBudgetDetails(e.target.value)}
          >
            <option value="" hidden>
              {t("selectBudgetRange")}
            </option>
            <option value="$50–$100">$50–$100</option>
            <option value="$101–$250">$101–$250</option>
            <option value="$251–$500">$251–$500</option>
            <option value="$500+">$500+</option>
          </select>
        </div>

        <div>
          <label className={labelClass}>
            {t("urgency")} <span className="text-red-500">*</span>
          </label>
          {onBrandSurface ? (
            <select
              className={controlClass}
              value={urgency}
              onChange={(e) =>
                setUrgency(e.target.value as "low" | "medium" | "high")
              }
              required
            >
              <option value="low">{t("low")}</option>
              <option value="medium">{t("medium")}</option>
              <option value="high">{t("high")}</option>
            </select>
          ) : (
            <div className="flex gap-3 w-full">
              {urgencyOptions.map((opt) => {
                const active = urgency === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setUrgency(opt)}
                    className={`flex-1 rounded-lg px-1 py-2 text-xs sm:text-sm text-center transition-colors ${
                      active
                        ? "bg-[#4285F4] text-[#ccd8ff]"
                        : "bg-[#f9f9ff] border border-[#d8e3fb] text-[#424654]"
                    }`}
                  >
                    {t(opt)}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div
          data-request-quote-invalid={fieldErrors.location ? "true" : undefined}
        >
          <label className={labelClass}>
            {t("serviceLocation")}
            <span className="text-red-500"> *</span>
          </label>
          <div className="mt-1 flex items-center gap-2">
            <div
              className={`flex-1 overflow-visible ${
                onBrandSurface
                  ? "rounded-lg bg-white shadow-sm"
                  : "rounded-lg border border-[#d8e3fb] bg-[#f9f9ff]"
              } ${fieldErrors.location ? "ring-2 ring-red-400 rounded-lg" : ""}`}
            >
              <GooglePlacesAutocomplete
                apiKey={googlePlacesApiKey}
                selectProps={{
                  value: googlePlace,
                  onChange: handleGooglePlaceSelect,
                  placeholder: t("search_location"),
                  isClearable: true,
                  menuPortalTarget:
                    typeof document !== "undefined" ? document.body : null,
                  styles: {
                    control: (base) => ({
                      ...base,
                      border: "none",
                      boxShadow: "none",
                      backgroundColor: "transparent",
                      minHeight: onBrandSurface ? 48 : 46,
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      paddingLeft: onBrandSurface ? 16 : 17,
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  },
                }}
                autocompletionRequest={{
                  componentRestrictions: { country: "PR" },
                }}
              />
            </div>
            <button
              type="button"
              className={`shrink-0 rounded-full border p-2 ${
                onBrandSurface
                  ? "bg-white border-transparent shadow-sm"
                  : "bg-white border-[#d8e3fb]"
              }`}
              onClick={() => setShowLocationPopup(true)}
              title={t("chooseLocationOnMap")}
              aria-label={t("chooseLocationOnMap")}
            >
              <LocationPinIcon className="w-6 h-6" />
            </button>
          </div>
          <FieldError
            message={fieldErrors.location}
            onBrandSurface={onBrandSurface}
          />
        </div>

        <div
          data-request-quote-invalid={fieldErrors.startDate ? "true" : undefined}
        >
          <label className={labelClass}>
            {t("select_start_date_time")} <span className="text-red-500">*</span>
          </label>
          <DatePicker
            selected={startDate}
            onChange={(date: Date | null) => {
              setStartDate(date);
              if (date) clearFieldError("startDate");
            }}
            showTimeSelect
            timeFormat="hh:mm aa"
            timeIntervals={15}
            dateFormat="MMMM d, yyyy h:mm aa"
            placeholderText={t("select_start_date_time") || "mm/dd/yyyy"}
            className={`${controlClass} ${invalidControlClass("startDate")}`}
            wrapperClassName="w-full"
            minDate={new Date()}
            minTime={getMinTime(startDate)}
            maxTime={getMaxTime(startDate)}
          />
          <FieldError
            message={fieldErrors.startDate}
            onBrandSurface={onBrandSurface}
          />
        </div>

        <div>
          <label className={labelClass}>{t("addPhotosOptional")}</label>
          <div
            className={
              onBrandSurface
                ? "rounded-xl border-2 border-dashed border-[#c3c6d6] bg-[#f0f3ff] p-2"
                : "rounded-lg border border-dashed border-[#d8e3fb] bg-[#f9f9ff] p-2"
            }
          >
            <DragDropImagePicker
              isRequired={false}
              onImageSelected={handleImageChange}
              acceptedFile={FileTypes.image}
              multiple
              maxFiles={5 - selectedImages.length}
            />
          </div>
          {imagePreviews.length > 0 && (
            <div className="flex flex-wrap gap-4 mt-3">
              {imagePreviews.map((preview, idx) => (
                <div key={idx} className="relative">
                  <img
                    src={preview}
                    alt=""
                    className="h-20 w-32 rounded-md border object-cover"
                  />
                  <button
                    type="button"
                    className="absolute -top-2 -right-2 w-5 h-5 bg-white rounded-full border text-sm"
                    onClick={() => handleRemoveImage(idx)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {isMobile && (
          <div className="flex gap-3 items-center rounded-lg bg-[#f0f3ff] p-3">
            <span className="text-[#4285F4] text-base" aria-hidden>
              ✓
            </span>
            <p className="text-xs font-medium text-[#424654]">
              {t("requestSecureNote")}
            </p>
          </div>
        )}

        {!hideSubmit && (
          <button
            type="submit"
            className={`w-full h-12 mt-2 font-semibold text-white rounded-xl ${
              isMobile ? "bg-[#4285F4]" : "bg-[#4285F4] rounded-3xl"
            }`}
          >
            {submitLabel}
          </button>
        )}
      </form>
    </>
  );
}
